<m-modal :title="title"
         ref="modal"
         :open.sync="propOpen"
         :close-on-backdrop="false"
         :padding-body="false"
         @open="onOpen"
         @close="onClose"
         @portal-after-open="onPortalAfterOpen">

    <template slot="trigger">
        <slot />
    </template>
    <div class="m-file-upload"
         v-m-file-drop.keep-store="storeName">
        <m-message v-if="hasRejectedFiles"
                   class="m-file-upload__error-message"
                   state="error"
                   :close-button="true"
                   ref="message"
                   @close="onMessageClose">
            <p class="m-file-upload__error-message__title">
                {{ $i18n.translate('m-file-upload:rejectFileMsg', [], rejectedFiles.length) }}
            </p>
            <ul class="m-file-upload__error-message__list">
                <li v-for="file in rejectedFiles"
                    :key="file.uid">
                    <p class="m-file-upload__error-message__file-name">{{file.name}}</p>
                    <p v-if="hasExtensionsRejection(file)"
                       class="m-file-upload__error-message__error">
                        {{ $i18n.translate('m-file-upload:extensionFileMsg') }}
                    </p>
                    <p v-else-if="hasSizeRejection(file)"
                       class="m-file-upload__error-message__error">
                       {{ $i18n.translate('m-file-upload:maxSizeMsg') }} ({{maxSizeKb * 1024 | f-m-filesize}}).
                    </p>
                    <p v-else-if="hasMaxFilesRejection(file)"
                       class="m-file-upload__error-message__error">
                        {{ $i18n.translate('m-file-upload:maxNumberMsg') }} ({{propMaxFiles}} {{ $i18n.translate('m-file-upload:file', [], propMaxFiles) }})
                    </p>
                    <p v-else-if="hasCustomValidationRejection(file)"
                       class="m-file-upload__error-message__error">
                        {{ customValidation.message }}
                    </p>
                </li>
            </ul>
        </m-message>

        <slot name="header" />

        <div v-if="isDropZoneEnabled" class="m-file-upload__drop-zone">
            <div class="m-file-upload__drop-zone__instructions">
                <h3 class="m-file-upload__drop-zone__instructions__title">
                    <m-icon name="m-svg__arrow--down"
                            size="32px" />
                    <span>{{ $i18n.translate('m-file-upload:dropFile', [], propMaxFiles) }}</span>
                </h3>
                <m-file-select class="m-file-upload__drop-zone__instructions__button"
                               skin="secondary"
                               :multiple="multipleSelection"
                               :store-name="storeName"
                               keep-store="true"
                               :allowed-extensions="allowedExtensions" />
            </div>
            <p v-if="hasAllowedExtensions"
               class="m-file-upload__drop-zone__format">
                <span class="m-file-upload__drop-zone__format__label">{{ $i18n.translate('m-file-upload:format', [], allowedExtensions.length, '', true, 'vsprintf') }}</span>: {{fileAllowedExtensions}}
            </p>
            <p v-if="selectionHelpMessage"
               class="m-file-upload__drop-zone__format">{{ selectionHelpMessage }}</p>
        </div>
        <div v-else
             class="m-file-upload__no-drop-zone">
            <h3 class="m-file-upload__no-drop-zone__title">
                {{ $i18n.translate('m-file-upload:selectFile', [], propMaxFiles) }}
            </h3>
            <m-file-select class="m-file-upload__no-drop-zone__instructions__button"
                           skin="primary"
                           :multiple="maxFiles > 1 ? true : false"
                           :store-name="storeName"
                           keep-store="true"
                           :allowed-extensions="allowedExtensions"></m-file-select>
            <p v-if="hasAllowedExtensions"
               class="m-file-upload__no-drop-zone__format">
                <span class="m-file-upload__no-drop-zone__format__label">{{ $i18n.translate('m-file-upload:format', [], allowedExtensions.length) }}</span>: {{fileAllowedExtensions}}
            </p>
            <p v-if="selectionHelpMessage"
               class="m-file-upload__drop-zone__format">{{ selectionHelpMessage }} </p>
        </div>

        <h3 v-if="!hasUploadingFiles" class="m-file-upload__title">
            {{ $i18n.translate('m-file-upload:import') }} ({{uploadingFiles.length}} {{ $i18n.translate('m-file-upload:file', [], uploadingFiles.length) }})
        </h3>

        <div v-if="!hasUploadingFiles" class="m-file-upload__import-list">
            <transition-group class="m-file-upload__import-list__wrap"
                              name="m--is"
                              tag="ul">
                <li v-for="(file, index) in uploadingFiles" :key="file.uid">
                    <div class="m-file-upload__import-list__content">
                        <div class="m-file-upload__import-list__file-icon">
                            <m-icon-file v-m-badge="{ state: getBadgeState(file) }"
                                         :extension="file.extension"
                                         size="30px"></m-icon-file>
                        </div>
                        <div class="m-file-upload__import-list__progress-wrap">
                            <div class="m-file-upload__import-list__infos">
                                <p class="m-file-upload__import-list__name">{{file.name}}</p>
                                <p class="m-file-upload__import-list__size">{{file.file.size | f-m-filesize}}</p>
                            </div>
                            <m-progress :value="file.progress"
                                        :state="getFileStatus(file)"></m-progress>
                            <p v-if="getFileStatus(file) === 'error'"
                               class="m-file-upload__import-list__error-message">
                                {{ $i18n.translate('m-file-upload:errorMsg') }}
                            </p>
                        </div>
                        <div class="m-file-upload__import-list__delete">
                            <m-icon-button class="m-file-upload__import-list__button"
                                           :title="tooltipCancel"
                                           icon-name="m-svg__close-clear"
                                           button-size="32px"
                                           icon-size="12px"
                                           @click="onUploadCancel(file)"
                                           ref="cancelUploadButton"></m-icon-button>
                        </div>
                    </div>
                </li>
            </transition-group>
        </div>
        <div v-if="!hasCompletedFiles"
             class="m-file-upload__completed-list"
             :class="{ 'm--has-border': !hasUploadingFiles }">
            <transition-group class="m-file-upload__completed-list__wrap"
                              name="m--is"
                              tag="ul">
                <li v-for="(file, index) in completedFiles" :key="file.uid">
                    <div class="m-file-upload__completed-list__content">
                        <div class="m-file-upload__completed-list__file-icon">
                            <m-icon-file v-m-badge="{ state: getBadgeState(file) }"
                                         :extension="file.extension"
                                         size="30px"></m-icon-file>
                        </div>
                        <div class="m-file-upload__completed-list__infos">
                            <p class="m-file-upload__completed-list__infos-label">
                                <m-link v-if="file.url"
                                        :url="file.url"
                                        mode="link"
                                        target="_blank">{{file.name}}</m-link>
                                <template v-else>{{file.name}}</template>
                            </p>
                            <p class="m-file-upload__completed-list__infos-size">{{file.file.size | f-m-filesize}}</p>
                        </div>
                        <div class="m-file-upload__completed-list__delete">
                            <m-icon-button class="m-file-upload__completed-list__button"
                                           :title="tooltipDelete"
                                           icon-name="m-svg__delete"
                                           button-size="32px"
                                           icon-size="20px"
                                           @click="onFileRemove(file)"
                                           ref="removeButton"></m-icon-button>
                        </div>
                    </div>
                </li>
            </transition-group>
        </div>
    </div>
    <template slot="footer">
        <div :style="buttonCompletedStyle">
            <m-button class="m-file-upload__footer-add"
                      :disabled="!isAddBtnEnabled"
                      ref="modalConfirmButton"
                      @click="onAddClick">{{ buttonAdd }}
                <span slot="precision"
                      v-show="completedFiles.length > 0">
                    <span>{{completedFiles.length}} {{ $i18n.translate('m-file-upload:file', [], completedFiles.length) }}</span>
                </span>
            </m-button>
            <m-button skin="secondary"
                      class="m-file-upload__footer-cancel"
                      ref="modalCancelButton"
                      @click="onCancelClick">
                {{ $i18n.translate('m-file-upload:cancel') }}
            </m-button>
        </div>
    </template>
</m-modal>
